// frontend/src/api/blog.ts
import { ApiError } from './auth';
import { API_BASE_URL } from '@/config/apiConfig';
import { createApiClient } from './client';
import {
  BlogPostListItem,
  BlogPostPublic,
  BlogPostAdmin,
  BlogPostCreatePayload,
  BlogPostUpdatePayload,
} from './schemas';

const blogApiClient = createApiClient({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

const authHeaders = (token: string | null | undefined): Record<string, string> => {
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
};

// ---------------------------------------------------------------------------
// Public
// ---------------------------------------------------------------------------

export const fetchBlogList = async (
  skip: number = 0,
  limit: number = 20
): Promise<BlogPostListItem[]> => {
  const url = `${API_BASE_URL}/blog?skip=${skip}&limit=${limit}`;
  try {
    const response = await blogApiClient.get<BlogPostListItem[]>(url);
    return response.data;
  } catch (error: any) {
    throw new ApiError('Failed to fetch blog posts.', error?.response?.status, { cause: error });
  }
};

export const fetchBlogPostBySlug = async (
  slug: string,
  opts?: { preview?: boolean; token?: string | null }
): Promise<BlogPostPublic> => {
  const preview = opts?.preview ? '?preview=1' : '';
  const url = `${API_BASE_URL}/blog/${encodeURIComponent(slug)}${preview}`;
  const headers = opts?.preview ? authHeaders(opts.token) : undefined;
  try {
    const response = await blogApiClient.get<BlogPostPublic>(url, { headers });
    return response.data;
  } catch (error: any) {
    throw new ApiError(
      error?.response?.status === 404 ? 'Blog post not found.' : 'Failed to fetch blog post.',
      error?.response?.status,
      {
        cause: error,
        userMessage: error?.response?.status === 404 ? 'Blog post not found.' : null,
      },
    );
  }
};

// ---------------------------------------------------------------------------
// Admin (superuser)
// ---------------------------------------------------------------------------

export const fetchAdminBlogList = async (
  token: string | null,
  skip: number = 0,
  limit: number = 100
): Promise<BlogPostAdmin[]> => {
  const url = `${API_BASE_URL}/admin/blog?skip=${skip}&limit=${limit}`;
  try {
    const response = await blogApiClient.get<BlogPostAdmin[]>(url, { headers: authHeaders(token) });
    return response.data;
  } catch (error: any) {
    throw new ApiError('Failed to fetch admin blog list.', error?.response?.status, { cause: error });
  }
};

export const fetchAdminBlogPost = async (
  postId: number,
  token: string | null
): Promise<BlogPostAdmin> => {
  const url = `${API_BASE_URL}/admin/blog/${postId}`;
  try {
    const response = await blogApiClient.get<BlogPostAdmin>(url, { headers: authHeaders(token) });
    return response.data;
  } catch (error: any) {
    throw new ApiError('Failed to fetch blog post.', error?.response?.status, { cause: error });
  }
};

export const createBlogPost = async (
  payload: BlogPostCreatePayload,
  token: string | null
): Promise<BlogPostAdmin> => {
  const url = `${API_BASE_URL}/admin/blog`;
  try {
    const response = await blogApiClient.post<BlogPostAdmin>(url, payload, { headers: authHeaders(token) });
    return response.data;
  } catch (error: any) {
    const detail = error?.response?.data?.detail;
    const status = error?.response?.status;
    const userMessage = typeof detail === 'string' && (status === 400 || status === 409)
      ? detail
      : null;
    throw new ApiError(
      typeof detail === 'string' ? detail : 'Failed to create post.',
      status,
      { cause: error, userMessage },
    );
  }
};

export const updateBlogPost = async (
  postId: number,
  payload: BlogPostUpdatePayload,
  token: string | null
): Promise<BlogPostAdmin> => {
  const url = `${API_BASE_URL}/admin/blog/${postId}`;
  try {
    const response = await blogApiClient.patch<BlogPostAdmin>(url, payload, { headers: authHeaders(token) });
    return response.data;
  } catch (error: any) {
    const detail = error?.response?.data?.detail;
    const status = error?.response?.status;
    const userMessage = typeof detail === 'string' && (status === 400 || status === 409)
      ? detail
      : null;
    throw new ApiError(
      typeof detail === 'string' ? detail : 'Failed to update post.',
      status,
      { cause: error, userMessage },
    );
  }
};

export const deleteBlogPost = async (postId: number, token: string | null): Promise<void> => {
  const url = `${API_BASE_URL}/admin/blog/${postId}`;
  try {
    await blogApiClient.delete(url, { headers: authHeaders(token) });
  } catch (error: any) {
    throw new ApiError('Failed to delete post.', error?.response?.status, { cause: error });
  }
};

export const publishBlogPost = async (
  postId: number,
  token: string | null
): Promise<BlogPostAdmin> => {
  const url = `${API_BASE_URL}/admin/blog/${postId}/publish`;
  try {
    const response = await blogApiClient.post<BlogPostAdmin>(url, {}, { headers: authHeaders(token) });
    return response.data;
  } catch (error: any) {
    throw new ApiError('Failed to publish post.', error?.response?.status, { cause: error });
  }
};

export const unpublishBlogPost = async (
  postId: number,
  token: string | null
): Promise<BlogPostAdmin> => {
  const url = `${API_BASE_URL}/admin/blog/${postId}/unpublish`;
  try {
    const response = await blogApiClient.post<BlogPostAdmin>(url, {}, { headers: authHeaders(token) });
    return response.data;
  } catch (error: any) {
    throw new ApiError('Failed to unpublish post.', error?.response?.status, { cause: error });
  }
};
